import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ManageAssetsStep} from '../Step/ManageAssetsStep';
import {CoverMode, CreateCoverStep} from '../Step/CreateCoverStep';
import {PreviewStep} from '../Step/PreviewStep';
import {UserFlowStep} from '../../../Facade/UserFlow/BaseUserFlow';
import {AssetMetaInterface} from '@taopix/taopix-core/dist/types/types';
import {MediaGalleryProps} from '../../MediaGallery/MediaGallery';
import {SubStep} from '../Step/SubStep/SubStep';
import {useTaopix} from '../../Context/TaopixContext';
import {LoadingOverlay} from '@taopix/taopix-design-system';
import {useUserFlowState} from '../../Context/UserFlow/UserFlowContext';
import {UserFlowType} from '../../../Facade/UserFlow/UserFlowFacade';
import {WelcomeStep} from '../Step/WelcomeStep';
import {AssistantCommonProps, AssistantHeaderProps, AssistantNavigationProps} from '../Assistant';
import {AssistantType, AutoFlowError} from '@taopix/taopix-core';
import {useExperience} from '../../Experience/ExperienceProvider';
import {UserFeedbackActions} from '../../Context/UserFeedback/UserFeedbackActionTypes';
import {useUserFeedback} from '../../Context/UserFeedback/UserFeedbackContext';

type BaseAssistantProps = {
  assets: AssetMetaInterface[];
  mediaGalleryProps: MediaGalleryProps;
  run: () => Promise<any>;
  onAfterRun: () => void;
  assistantType?: AssistantType;
} & AssistantCommonProps &
  AssistantNavigationProps &
  AssistantHeaderProps;

export const BaseAssistant = ({run, onAfterRun, assistantType, ...props}: BaseAssistantProps) => {
  const taopix = useTaopix();
  const userFlowState = useUserFlowState();
  const userFeedback = useUserFeedback();
  const {experience} = useExperience();
  const {t} = useTranslation(['assistants']);
  const [optionsOpen, setOptionsOpen] = useState<boolean>(false);
  const [loadingOpen, setLoadingOpen] = useState<boolean>(false);

  const getNextButton = (): React.ReactElement => {
    return React.cloneElement(props.nextButton, {
      ...props.nextButton.props,
      onClick: () => {
        const currentStep = userFlowState.getCurrentStep(UserFlowType.Assistant);
        const nextStepResult: string = userFlowState.getNextStep(UserFlowType.Assistant);

        if (
          [
            `${UserFlowType.Assistant}.${UserFlowStep.AssistantManageAssets}`,
            `${UserFlowType.Assistant}.${UserFlowStep.AssistantFrontCover}`,
            `${UserFlowType.Assistant}.${UserFlowStep.AssistantBackCover}`,
            `${UserFlowType.Assistant}.${UserFlowStep.AssistantSmartSelection}`,
          ].includes(currentStep)
        ) {
          if (
            [
              `${UserFlowType.Assistant}.${UserFlowStep.AssistantPreview}`,
              `${UserFlowType.Assistant}.${UserFlowStep.None}`,
            ].includes(nextStepResult)
          ) {
            // If next step is the preview, or if preview is off, open the option dialog.
            // This will trigger the action to execute to create the project, either immediately or
            // after all the option screens have been completed.
            setOptionsOpen(true);
          } else {
            props.nextButton.props.onClick();
          }
        } else {
          props.nextButton.props.onClick();
        }
      },
    });
  };

  const subStepPanel = (
    <SubStep
      open={optionsOpen}
      onLastStep={() => {
        setOptionsOpen(false);
        setLoadingOpen(true);

        taopix.core
          .get('stateManager')
          .call('autosave')
          .then(() => {
            run()
              .then(response => {
                if (response) {
                  onAfterRun();
                  props.nextButton.props.onClick();
                }
              })
              .catch(error => {
                userFeedback.dispatch(
                  UserFeedbackActions.setErrorUserFeedback({
                    title: '',
                    message: error.message,
                    showHeader: !(error instanceof AutoFlowError),
                  })
                );
              })
              .finally(() => {
                setLoadingOpen(false);
              });
          })
          .catch(error => {
            userFeedback.dispatch(
              UserFeedbackActions.setErrorUserFeedback({
                title: '',
                message: error.message,
                showHeader: true,
              })
            );
            setLoadingOpen(false);
          });
      }}
      onClose={() => setOptionsOpen(false)}
    />
  );

  let currentStep = <></>;
  switch (true) {
    case userFlowState.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantWelcome): {
      currentStep = (
        <WelcomeStep
          {...props}
          nextButton={getNextButton()}
          onClose={props.onClose}
          assistantType={assistantType}
        />
      );
      break;
    }
    case userFlowState.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantManageAssets): {
      currentStep = <ManageAssetsStep {...props} nextButton={getNextButton()} />;
      break;
    }
    case userFlowState.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantFrontCover): {
      currentStep = (
        <CreateCoverStep
          {...props}
          key={`cover-step-${CoverMode.Front}`}
          coverMode={CoverMode.Front}
          nextButton={getNextButton()}
          canShowTextDefaultMode={!optionsOpen && !loadingOpen}
        />
      );
      break;
    }
    case userFlowState.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantBackCover): {
      currentStep = (
        <CreateCoverStep
          {...props}
          key={`cover-step-${CoverMode.Back}`}
          coverMode={CoverMode.Back}
          nextButton={getNextButton()}
          canShowTextDefaultMode={!optionsOpen && !loadingOpen}
        />
      );
      break;
    }
    case userFlowState.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantPreview): {
      currentStep = <PreviewStep {...props} />;
      break;
    }
  }

  return (
    <>
      {currentStep}
      {subStepPanel}
      <LoadingOverlay
        id={'base-assistant'}
        open={loadingOpen}
        label={(() => {
          let labelKey = '*:str_TitleCreatingProject';
          if (
            AssistantType.PictureSelectionOnly ===
              experience.WIZARD.designAssistantType.designAssistantType &&
            experience.WIZARD.pictureSelectionOnlyOptions.autoFlowSelectedPicturesAtProjectStart
          ) {
            labelKey = '*:str_TitleApplyAutofill';
          }

          const label = t(labelKey);
          return `${label[0].toUpperCase()}${label.slice(1).toLowerCase()}`;
        })()}
      />
    </>
  );
};
